//
// Copyright © 2026 Hardcore Engineering Inc.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//

import { AccountRole } from '@hcengineering/core'

/**
 * Pure helpers for the issue/chat-session link. No I/O, no platform access, so the same
 * rules can be reused by the UI, by migrations and by server-side callers.
 *
 * @public
 */

/**
 * The four fields that identify one external chat session.
 * @public
 */
export interface ChatSessionTuple {
  source: string
  sessionId: string
  executionEnvironment: string
  userName: string
}

/**
 * Ordered list of chat-session identity fields. The order is part of the storage contract:
 * it is the column order of the uniqueness/lookup indexes.
 * @public
 */
export const chatSessionTupleFields: Array<keyof ChatSessionTuple> = [
  'source',
  'sessionId',
  'executionEnvironment',
  'userName'
]

/**
 * Result of parsing an untrusted chat-session tuple.
 * @public
 */
export type ChatSessionTupleParseResult =
  | { ok: true, value: ChatSessionTuple }
  | { ok: false, invalidFields: Array<keyof ChatSessionTuple> }

/**
 * Parse an untrusted chat-session tuple.
 *
 * All four fields are required and may not consist of whitespace only. Accepted values are
 * passed through byte for byte — no trimming, no case folding, no reformatting — because
 * these are opaque identifiers owned by the source system and rewriting them could make two
 * distinct sessions collide. `trim()` is used to decide whether a value is blank, never to
 * produce the stored value.
 *
 * @public
 */
export function parseChatSessionTuple (
  input: Partial<Record<keyof ChatSessionTuple, unknown>>
): ChatSessionTupleParseResult {
  const value: Partial<ChatSessionTuple> = {}
  const invalidFields: Array<keyof ChatSessionTuple> = []

  for (const field of chatSessionTupleFields) {
    const raw = input[field]
    if (typeof raw !== 'string' || raw.trim() === '') {
      invalidFields.push(field)
    } else {
      value[field] = raw
    }
  }

  if (invalidFields.length > 0) {
    return { ok: false, invalidFields }
  }
  return { ok: true, value: value as ChatSessionTuple }
}

/**
 * Whether two tuples identify the same chat session.
 *
 * Byte for byte over all four fields, with no trimming and no case folding — the same comparison
 * the uniqueness index makes, so two values that differ only by surrounding whitespace are two
 * different sessions here as well. Used by the add form to decide whether the values still in the
 * fields are the ones the server refused as a duplicate.
 *
 * @public
 */
export function sameChatSessionTuple (left: ChatSessionTuple, right: ChatSessionTuple): boolean {
  return chatSessionTupleFields.every((field) => left[field] === right[field])
}

/**
 * Filter of the reverse search: the raw session id, narrowed by any subset of the rest.
 *
 * Only `sessionId` is required — it is what a caller actually has in hand, while the other
 * three are context it may not know. Each present field is an exact, byte-for-byte equality
 * filter and they combine with AND, so every subset from "session id alone" to all four is a
 * valid query. A field is absent exactly when it does not constrain anything.
 * @public
 */
export interface ChatSessionSearchFilter {
  sessionId: string
  source?: string
  executionEnvironment?: string
  userName?: string
}

/** The three fields of the search filter that may be left out. @public */
export const chatSessionOptionalFilterFields: Array<keyof ChatSessionTuple> = [
  'source',
  'executionEnvironment',
  'userName'
]

/**
 * Result of parsing an untrusted reverse-search form.
 * @public
 */
export type ChatSessionSearchFilterParseResult =
  | { ok: true, value: ChatSessionSearchFilter }
  | { ok: false, invalidFields: Array<keyof ChatSessionTuple> }

/**
 * Parse the reverse-search form.
 *
 * `sessionId` is required and may not be whitespace-only; a missing or blank one is the only
 * rejection. The other three are dropped when blank — a form field the user left alone, or
 * filled with spaces and then cleared, means "do not filter on this", not "match the empty
 * string", and no stored value is blank anyway. Values that are kept are passed through byte
 * for byte, so a session id that really does carry surrounding whitespace is searchable:
 * `trim()` decides only whether a field constrains anything, never what is searched for.
 *
 * @public
 */
export function parseChatSessionSearchFilter (
  input: Partial<Record<keyof ChatSessionTuple, unknown>>
): ChatSessionSearchFilterParseResult {
  const rawId = input.sessionId
  if (typeof rawId !== 'string' || rawId.trim() === '') {
    return { ok: false, invalidFields: ['sessionId'] }
  }

  const value: ChatSessionSearchFilter = { sessionId: rawId }
  for (const field of chatSessionOptionalFilterFields) {
    const raw = input[field]
    if (typeof raw === 'string' && raw.trim() !== '') {
      value[field] = raw
    }
  }
  return { ok: true, value }
}

/** Whether one stored tuple satisfies the reverse-search filter. @public */
export function chatSessionMatchesSearchFilter (tuple: ChatSessionTuple, filter: ChatSessionSearchFilter): boolean {
  if (tuple.sessionId !== filter.sessionId) return false
  return chatSessionOptionalFilterFields.every((field) => {
    const expected = filter[field]
    return expected === undefined || tuple[field] === expected
  })
}

/** Two filters are the same query only when the same fields carry the same exact values. @public */
export function sameChatSessionSearchFilter (
  left: ChatSessionSearchFilter,
  right: ChatSessionSearchFilter
): boolean {
  return (
    left.sessionId === right.sessionId &&
    chatSessionOptionalFilterFields.every((field) => left[field] === right[field])
  )
}

/**
 * Literal, case-insensitive substring match against the session id. An empty or
 * whitespace-only query matches everything; any other value is matched verbatim, so `%`,
 * `_` and `\` mean exactly themselves.
 * @public
 */
export function chatSessionMatchesQuery (tuple: ChatSessionTuple, query: string): boolean {
  if (query.trim() === '') return true
  return tuple.sessionId.toLowerCase().includes(query.toLowerCase())
}

/**
 * Filter state of the in-card chat-session block. A blank value means "no constraint".
 * @public
 */
export type ChatSessionFilter = Partial<Record<keyof ChatSessionTuple, string>>

/**
 * Whether one session passes the in-card filter.
 *
 * `sessionId` is a literal substring search; the other three are exact matches, because they
 * narrow the search down to a specific source, environment or user. Nothing is trimmed
 * before comparison: stored identity values are kept byte for byte, so trimming here would
 * fail to find a session whose value really does carry surrounding whitespace. `trim()`
 * decides only whether a field constrains anything at all.
 *
 * This runs over the loaded sessions rather than as a query predicate on purpose. Huly's
 * `$like` cannot express a literal substring: the SQL adapter emits `ILIKE`, where both `%`
 * and `_` are wildcards escaped by backslash, while the client-side matcher turns `%` into
 * `.*` and honours no escape character at all. An opaque session id such as `S_1%` would
 * therefore match unrelated sessions, and differently so depending on which matcher ran.
 *
 * @public
 */
export function chatSessionMatchesFilter (tuple: ChatSessionTuple, filter: ChatSessionFilter): boolean {
  if (!chatSessionMatchesQuery(tuple, filter.sessionId ?? '')) return false
  for (const field of ['source', 'executionEnvironment', 'userName'] as const) {
    const expected = filter[field] ?? ''
    if (expected.trim() !== '' && tuple[field] !== expected) return false
  }
  return true
}

/** Whether the filter constrains anything at all. */
export function isChatSessionFilterActive (filter: ChatSessionFilter): boolean {
  return chatSessionTupleFields.some((field) => (filter[field] ?? '').trim() !== '')
}

/**
 * How an attach ended.
 *
 * `unavailable` is the one that is not a success: the parent issue was not there when the attach
 * was evaluated, so no link was inserted and the issue's `chatSessions` counter did not move. It
 * must never be reported as created — a link whose parent is absent is an orphan row, which is
 * what a read-then-write attach produced live when the issue was deleted while the form was open.
 *
 * There is no result for "refused for some other reason": that is raised, not returned, so a
 * caller cannot mistake it for one of the three outcomes above.
 *
 * @public
 */
export type ChatSessionAttachResult<L> =
  | { kind: 'created', id: L }
  | { kind: 'existing', id: L }
  | { kind: 'unavailable' }

/**
 * The insert was refused for a reason this code cannot name: the parent is still there, so the
 * condition held, and no link carries this tuple, so it was not a duplicate.
 *
 * Raised only when the refusal arrived with no error to rethrow, which is the browser's shape of
 * a refused write — see `attachChatSessionLink`. The caller must surface it as a failure: the
 * write did not happen, and why it did not is not known here.
 *
 * @public
 */
export class ChatSessionAttachRefusedError extends Error {
  constructor () {
    super('tracker: the chat-session insert was refused, and neither the parent nor a duplicate explains it')
    this.name = 'ChatSessionAttachRefusedError'
  }
}

/**
 * The write and the two reads an attach needs, as a port so the decision below can be exercised
 * without a backend. Implementations are thin SDK adapters; the rules live here.
 *
 * Both reads must be answered by the server. They are the evidence a refusal is classified from,
 * so a client-side cache answering them would decide the outcome from state the server never
 * confirmed — which is how the orphan link this path exists to prevent came about.
 *
 * @public
 */
export interface ChatSessionAttachPort<L> {
  /**
   * Insert the link as one server-evaluated conditional step: the insert applies only if the
   * parent is still there at that moment.
   *
   * `{ committed: false }` means nothing was written. It does *not* identify why: depending on
   * the client it covers both a condition that did not hold and a write the backend refused.
   * A rejection also means nothing was written, and carries the backend's reason.
   */
  insertIfParentPresent: (tuple: ChatSessionTuple) => Promise<{ committed: boolean, id: L }>
  /** Whether the parent is still there, under the same identity the insert conditions on. */
  isParentPresent: () => Promise<boolean>
  /** The link already carrying this tuple on this parent, if there is one. */
  findExisting: (tuple: ChatSessionTuple) => Promise<L | undefined>
}

/**
 * Attach one chat session to an issue, idempotently, without ever producing an orphan.
 *
 * There is no pre-read. A read followed by an unconditional insert is two steps, and the gap
 * between them is exactly where the issue was deleted live: the insert then wrote a link whose
 * parent no longer existed and the form reported success. A client-side pre-read cannot close
 * that gap, and in the browser it cannot even be trusted to notice, because the issue is served
 * from the live-query document cache the deletion never reached in that session.
 *
 * The server therefore decides whether anything was written, and the insert applying is the one
 * answer that needs nothing further: that is `created`, and no read is made.
 *
 * A refusal is harder, because its reason does not survive the trip home in every client. The
 * backend answers a condition that did not hold with `success: false` and lets a refused write —
 * the uniqueness index rejecting a duplicate, a permission verdict — throw. Whether that throw
 * reaches this code depends on the client: the REST SDK propagates it, so there a duplicate is a
 * rejection, while in the browser `PresentationPipelineImpl.tx` catches the backend error and
 * returns an empty result, which `ApplyOperations.commit` reads as a failed apply. The very same
 * duplicate then arrives as `committed: false`, indistinguishable from the parent having gone.
 * Observed live: re-adding an existing tuple from the card reported the issue as unavailable
 * while the issue, the link and the counter were all intact.
 *
 * So both shapes of refusal are classified the same way, from server-fresh evidence, in this
 * order:
 *
 *  - the parent is no longer there — deleted, or moved out of the space the insert conditions on
 *    — and the answer is `unavailable`. Asked first and answered on its own, so a link that
 *    exists while its parent does not, whether a stale row or a stale cache entry, can never be
 *    reported as a success. This is never a permission verdict;
 *  - the parent is there and a link carries this exact tuple: `existing`. This caller wrote
 *    nothing, and the state it asked for is the state that holds — whether it asked for a
 *    duplicate or lost a race to an identical concurrent attach;
 *  - the parent is there and no such link: the refusal is something this code does not model —
 *    a permission verdict, a transport failure, another constraint. The original rejection is
 *    rethrown unchanged when there was one, so a refused write keeps looking like a refusal, and
 *    `ChatSessionAttachRefusedError` is raised when the error was swallowed before it got here.
 *
 * The reads only classify. They run only after the server refused, they can turn a refusal into a
 * different refusal or into `existing` but never into `created`, and nothing here retries the
 * insert, writes anything to probe for existence, or re-counts the collection. An unanswerable
 * probe leans the way the write did: `isParentPresent` resolving false ends as a denial.
 *
 * @public
 */
export async function attachChatSessionLink<L> (
  port: ChatSessionAttachPort<L>,
  tuple: ChatSessionTuple
): Promise<ChatSessionAttachResult<L>> {
  let inserted: { committed: boolean, id: L }
  try {
    inserted = await port.insertIfParentPresent(tuple)
  } catch (err) {
    // Wrapped rather than passed as the value itself: a backend may reject with `undefined`, and
    // that still has to be rethrown rather than replaced.
    return await classifyRefusedChatSessionInsert(port, tuple, { err })
  }
  if (inserted.committed) return { kind: 'created', id: inserted.id }
  return await classifyRefusedChatSessionInsert(port, tuple, undefined)
}

/**
 * Decide what a refused insert means. See `attachChatSessionLink` for why this is needed at all
 * and why the order of the two reads is what it is.
 *
 * `rejection` is present when the refusal arrived as a rejection, and absent when it arrived as
 * `committed: false`. It changes nothing about the classification — only what is raised when the
 * refusal stays unexplained.
 */
async function classifyRefusedChatSessionInsert<L> (
  port: ChatSessionAttachPort<L>,
  tuple: ChatSessionTuple,
  rejection: { err: unknown } | undefined
): Promise<ChatSessionAttachResult<L>> {
  if (!(await port.isParentPresent())) return { kind: 'unavailable' }
  const existing = await port.findExisting(tuple)
  if (existing !== undefined) return { kind: 'existing', id: existing }
  if (rejection !== undefined) throw rejection.err
  throw new ChatSessionAttachRefusedError()
}

/**
 * One issue of the reverse-search result, with the links it matched through.
 * @public
 */
export interface ChatSessionIssueGroup<L> {
  attachedTo: string
  links: L[]
}

/**
 * Group matching links into one entry per issue.
 *
 * Input must be ordered by issue, which makes one issue's links contiguous; order is preserved
 * both between groups and within one. Says nothing about completeness — see
 * `planChatSessionIssuePage`, which decides which of these groups are safe to display.
 *
 * @public
 */
export function groupChatSessionLinksByIssue<L extends { attachedTo: string }> (
  links: L[]
): Array<ChatSessionIssueGroup<L>> {
  const groups: Array<ChatSessionIssueGroup<L>> = []
  for (const link of links) {
    const current = groups[groups.length - 1]
    if (current !== undefined && current.attachedTo === link.attachedTo) {
      current.links.push(link)
    } else {
      groups.push({ attachedTo: link.attachedTo, links: [link] })
    }
  }
  return groups
}

/**
 * One page of the reverse search, planned over a loaded window of links.
 * @public
 */
export interface ChatSessionIssuePagePlan<L> {
  /** Issues to display: at most `issueLimit`, each with every session it matched. */
  groups: Array<ChatSessionIssueGroup<L>>
  /** Matching issues exist beyond the ones in `groups`. */
  hasMore: boolean
  /**
   * The window is too small to decide this page and must be enlarged before it is rendered.
   *
   * The caller refetches with a larger link window and re-plans. It always becomes false
   * eventually: the window either grows past the number of links the filter matches — at which
   * point the query returns fewer links than were asked for and the window is complete — or it
   * grows past the point where `issueLimit + 1` issues have been fully read.
   */
  needsLargerWindow: boolean
}

/**
 * Decide which issues a page shows, given a window of links loaded in issue order.
 *
 * The page unit is the *issue*, not the link: it shows up to `issueLimit` issues and every one
 * of them carries all of the sessions it matched. Both halves of that need care at the window
 * edge, because the window is a number of *links*:
 *
 *  - the last group in the window may be cut in half by the window edge, so it is not
 *    displayable until the window is known to contain all of its links;
 *  - a window that ends exactly after `issueLimit` groups cannot tell whether a further issue
 *    exists, so `hasMore` would be a guess.
 *
 * `windowComplete` says the window holds every matching link, which the caller knows because
 * the query returned fewer links than it asked for. When it does, every group is complete and
 * the page is decided. When it does not, only the groups before the last one are known to be
 * complete; if there are at least `issueLimit + 1` of those, the page is still decided —
 * otherwise the caller must enlarge the window, which is what `needsLargerWindow` asks for.
 * That covers the single-issue case too: one issue filling the whole window simply grows it
 * until its links are all loaded, with no cutoff and no action required from the user.
 *
 * @public
 */
export function planChatSessionIssuePage<L extends { attachedTo: string }> (
  links: L[],
  issueLimit: number,
  windowComplete: boolean
): ChatSessionIssuePagePlan<L> {
  const groups = groupChatSessionLinksByIssue(links)

  if (windowComplete) {
    return {
      groups: groups.slice(0, issueLimit),
      hasMore: groups.length > issueLimit,
      needsLargerWindow: false
    }
  }

  // The final group may continue past the window edge, so it is not known to be complete.
  // Its mere existence, though, does prove a further issue — which is what decides `hasMore`
  // once enough complete groups are in hand.
  const complete = groups.slice(0, -1)
  if (complete.length >= issueLimit) {
    return { groups: complete.slice(0, issueLimit), hasMore: true, needsLargerWindow: false }
  }
  return { groups: [], hasMore: false, needsLargerWindow: true }
}

/**
 * One link request: how many links were asked for, and which attempt asked.
 *
 * `generation` exists because `window` alone cannot identify a request. Applying a new filter
 * resets the window to its initial value, so a late answer to the previous filter's first
 * request would carry the very same window as the new one and would be indistinguishable from
 * it. The generation only ever increases, so it tells them apart.
 *
 * @public
 */
export interface ChatSessionLinkRequest {
  generation: number
  window: number
}

/**
 * A link response, tagged with the request it answers.
 * @public
 */
export interface ChatSessionLinkResponse<L> extends ChatSessionLinkRequest {
  links: L[]
}

/**
 * Everything needed to decide what the reverse search can show right now.
 * @public
 */
export interface ChatSessionSearchPlanning<L> {
  /** The request currently outstanding. Any response not matching it is stale. */
  request: ChatSessionLinkRequest
  /** Issues the page may show. Raised by "show more"; does not re-request links by itself. */
  issueLimit: number
  /** The newest accepted response, or undefined while the current request is unanswered. */
  response: ChatSessionLinkResponse<L> | undefined
}

/**
 * What the view should do with the current planning state.
 *
 * `pending` means nothing may be rendered and nothing may be concluded: either no response has
 * arrived for the outstanding request, or the only response in hand answers an older one.
 *
 * @public
 */
export type ChatSessionSearchOutcome<L> =
  | { kind: 'pending' }
  | { kind: 'grow' }
  | { kind: 'ready', groups: Array<ChatSessionIssueGroup<L>>, hasMore: boolean }

/** First planning state for a freshly applied filter. @public */
export function beginChatSessionSearch<L> (issueLimit: number, window: number): ChatSessionSearchPlanning<L> {
  return { request: { generation: 1, window }, issueLimit, response: undefined }
}

/**
 * Start over for a new filter: a new generation, the initial window, and no response.
 * @public
 */
export function restartChatSessionSearch<L> (
  planning: ChatSessionSearchPlanning<L>,
  issueLimit: number,
  window: number
): ChatSessionSearchPlanning<L> {
  return { request: { generation: planning.request.generation + 1, window }, issueLimit, response: undefined }
}

/**
 * Enlarge the window.
 *
 * The response is dropped, not kept: it answered the smaller request, and keeping it would let
 * the view treat it as the answer to the larger one — which is exactly how a saturated window
 * came to look exhausted (its row count is below the *new* window) and a half-read issue came
 * to be rendered as complete. Clearing it puts the state back to `pending` until the enlarged
 * request is answered.
 *
 * @public
 */
export function growChatSessionSearchWindow<L> (
  planning: ChatSessionSearchPlanning<L>,
  growth: number
): ChatSessionSearchPlanning<L> {
  return {
    request: { generation: planning.request.generation + 1, window: planning.request.window * growth },
    issueLimit: planning.issueLimit,
    response: undefined
  }
}

/**
 * Show one more page of issues.
 *
 * The response is kept: a larger issue limit can often be served from the links already
 * loaded, and only `resolveChatSessionSearch` decides whether more are needed.
 *
 * @public
 */
export function showMoreChatSessionIssues<L> (
  planning: ChatSessionSearchPlanning<L>,
  step: number
): ChatSessionSearchPlanning<L> {
  return { ...planning, issueLimit: planning.issueLimit + step }
}

/**
 * Whether a response answers the request that is outstanding.
 *
 * Both fields have to match. The generation alone would be enough only as long as every change
 * of window also bumps the generation, which is true of the transitions here but is a property
 * of this module rather than of the contract — and a response carrying a different window
 * cannot decide anything regardless of how it came about, because completeness is measured
 * against the window that was asked for.
 *
 * @public
 */
export function answersChatSessionLinkRequest (
  request: ChatSessionLinkRequest,
  response: ChatSessionLinkRequest
): boolean {
  return response.generation === request.generation && response.window === request.window
}

/**
 * Take a link response, ignoring one that answers a superseded request.
 *
 * A live query can call back after its subscription was replaced — by a window growth or by a
 * new filter — and that answer describes a question nobody is asking any more.
 *
 * @public
 */
export function acceptChatSessionLinkResponse<L> (
  planning: ChatSessionSearchPlanning<L>,
  response: ChatSessionLinkResponse<L>
): ChatSessionSearchPlanning<L> {
  if (!answersChatSessionLinkRequest(planning.request, response)) return planning
  return { ...planning, response }
}

/**
 * Decide the page from the planning state.
 *
 * Completeness is judged against the window the *response* was requested with, never against
 * the window currently outstanding. Those differ for exactly as long as a growth is in flight,
 * and during that gap the old row count sits below the new window and would read as "all the
 * links there are" — so the planner would mark a cut-off issue complete and the view would
 * render partial sessions and stop its spinner. Tagging the response removes the gap: until it
 * answers the outstanding request, the only honest answer is `pending`.
 *
 * @public
 */
export function resolveChatSessionSearch<L extends { attachedTo: string }> (
  planning: ChatSessionSearchPlanning<L>
): ChatSessionSearchOutcome<L> {
  const response = planning.response
  if (response === undefined || !answersChatSessionLinkRequest(planning.request, response)) {
    return { kind: 'pending' }
  }

  const windowComplete = response.links.length < response.window
  const plan = planChatSessionIssuePage(response.links, planning.issueLimit, windowComplete)
  if (plan.needsLargerWindow) return { kind: 'grow' }
  return { kind: 'ready', groups: plan.groups, hasMore: plan.hasMore }
}

/**
 * Identity of a planned page, for matching an issue resolution to the plan it belongs to.
 *
 * The issues are fetched in a second query, so its answer can arrive after the plan it was
 * issued for has been replaced — by "show more", by a window growth or by a new filter. The
 * key is the plan's issue ids in order, which is precisely what that query asked about, so
 * comparing it is what keeps a previous page's issues from being rendered as this one's.
 *
 * @public
 */
export function chatSessionIssueKey<L> (groups: Array<ChatSessionIssueGroup<L>>): string {
  return groups.map((group) => group.attachedTo).join(' ')
}

/**
 * Position of a link in the ordered, filtered list, or -1 when it is not there yet.
 * @public
 */
export function chatSessionIndexOf (orderedIds: string[], target: string): number {
  return orderedIds.indexOf(target)
}

/**
 * How many rows must be shown for `targetIndex` to be on screen.
 *
 * Paging stays page-aligned and only ever grows: a link added to an issue that already has 29
 * links sorts to position 29 and would sit behind the first page, so revealing it means showing
 * the page it actually falls on — not re-sorting the list and not resetting the window.
 *
 * @public
 */
export function chatSessionShownForReveal (targetIndex: number, shown: number, pageSize: number): number {
  if (targetIndex < 0 || targetIndex < shown) return shown
  return Math.ceil((targetIndex + 1) / pageSize) * pageSize
}

/**
 * Whether an account in this role may change chat-session links at all.
 *
 * `canEditIssue` answers yes for any guest that collaborates on the issue, which is right for a
 * guest invited to work on it but wrong for `ReadOnlyGuest`: that role exists to grant reading
 * and nothing else, so a read-only guest added as a collaborator would otherwise be offered the
 * add and remove affordances. Only that one role is excluded here — narrowing `Guest` or
 * `DocGuest` would change behaviour this review did not ask about — and the backend remains the
 * authority either way.
 *
 * @public
 */
export function chatSessionMutationsAllowedForRole (role: AccountRole): boolean {
  return role !== AccountRole.ReadOnlyGuest
}

/**
 * Signature of what defines the in-card view: the issue, and the four filter values.
 *
 * Keyed on values rather than on the issue object. A Svelte statement that reads `issue._id`
 * depends on the whole prop, and the prop is re-assigned whenever the issue document changes —
 * including when its `chatSessions` counter moves because a link was just added. Resetting the
 * paging window on that invalidation undid a reveal that had already happened. Comparing this
 * string instead means a counter update produces an identical signature and resets nothing.
 *
 * `JSON.stringify` of a fixed-length array of these five values is unambiguous: the quoting and
 * escaping keep any separator or quote inside a value from colliding with another arrangement.
 *
 * @public
 */
export function chatSessionViewSignature (issueId: string, filter: ChatSessionFilter): string {
  return JSON.stringify([
    issueId,
    filter.sessionId,
    filter.source,
    filter.executionEnvironment,
    filter.userName
  ])
}
